import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Constants } from '@finxone-platform/shared/sys-config-types';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { defaultValidationMessages } from '../../../Modules/shared/input-field-widget/default-validation-messages';

export function customValidator(validatorFn: ValidatorFn): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    return validatorFn(control);
  };
}

export function validDayOfMonthYearValidator(
  yearControlName: string,
  monthControlName: string,
  dayControlName: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const year = control.get(yearControlName)?.value;
    const month = control.get(monthControlName)?.value;
    const day = control.get(dayControlName)?.value;
    const maxDaysInMonth = month <= 11 ? 32 - new Date(year, month, 32).getDate() : NaN;
    return day > maxDaysInMonth
      ? {
          error: defaultValidationMessages.invalidDayOfMonthYear(
            day,
            Constants.findMonthNameByKey(month),
            year,
          ),
        }
      : null;
  };
}

export function checkMinAgeValidation(minage: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.pristine) {
      return null;
    }

    const { day, month, year } = control.value || {};
    const d = Number(day);
    const m = Number(month + 1); // Assumed to be 0-based (January = 0)
    const y = Number(year);
    // Dayjs months are 0-based when using dayjs(new Date()), but not when using `dayjs("YYYY-MM-DD")`
    // So construct date string safely
    const birthDate = dayjs(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

    // Check if it's a valid date
    if (
      !birthDate.isValid() ||
      birthDate.year() !== y ||
      birthDate.month() + 1 !== m ||
      birthDate.date() !== d
    ) {
      return {
        error: defaultValidationMessages.invalidDate,
      };
    }

    // Subtract minAge years from today
    const minAgeYearsAgo = dayjs().subtract(minage, 'year');

    dayjs.extend(isSameOrBefore);

    // Compare birthdate to see if it's on or before that
    return birthDate.isSameOrBefore(minAgeYearsAgo, 'day')
      ? null
      : {
          error: defaultValidationMessages.minAge(minage),
        };
  };
}

export function noNumbersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const hasNumber = /\d/.test(control.value);
    return hasNumber ? { noNumbers: true } : null;
  };
}

export function noSpecialCharsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isValid = nameRegex.test(control.value);
    return isValid ? null : { noSpecialChars: true };
  };
}

export const nameRegex = /^(?!.*--)[\p{L}\p{N}\s'-]*$/u;

/**
 * Removes all asterisks (*) from the provided string.
 * @param value The string to process.
 * @returns The string without any asterisks.
 */
export function removeAsterisk(value: string): string {
  return value.replace(/\*/g, '');
}
